import { z } from 'zod';

import {
  IntegrationConfigResponseSchema,
  IntegrationGroup,
  IntegrationName,
  Integrations,
  IntegrationType,
  integrationUrl,
} from './integration';
import { type IRestEndpointSchema, makeEndpoint } from '../common';

export enum ANALYTICS_EVENT {
  VIEW_HOME = 'view_home',
  VIEW_COLLECTION = 'view_collection',
  VIEW_HOSTS = 'view_hosts',
  VIEW_EVENTS = 'view_activates',
  VIEW_ACTIVITIES = 'view_activities',
  VIEW_ADDONS = 'view_addons',
  CLICK_EXPERIENCE = 'click_experience',
  CLICK_FILTER = 'click_filter',
  /** A top-level tab swap on the storefront: Browse, Schedule or one of the brand's pages. */
  CLICK_TAB = 'click_tab',
  VIEW_CALENDAR = 'view_calendar',
  VIEW_EXPERIENCE_DETAIL = 'view_experience_detail',
  RESERVABLE_EXPERIENCE_CLICK_BOOK_BUTTON = 'reservable_experience_click_book_button',
  OPEN_EXPERIENCE_CLICK_BOOK_BUTTON = 'open_experience_click_book_button',
  VIEW_PARTICIPANT_INFO = 'view_participant_info',
  CLICK_ADD_TO_CART_BUTTON = 'click_add_to_cart_button',
  CLICK_PROCEED_TO_CHECKOUT_BUTTON = 'click_proceed_to_checkout_button',
  VIEW_CHECKOUT = 'view_checkout',
  VIEW_BOOKING_CONFIRMATION = 'view_booking_confirmation',
  /** A recommendation screen rendered its cards (confirmation page, recommendations page, dead-link page). First-party only. */
  VIEW_RECOMMENDATIONS = 'view_recommendations',
  CLICK_COMPLETE_PAYMENT_BUTTON = 'click_complete_payment_button',
  PAGE_STOP_ERROR = 'page_stop_error',
  FORM_VALIDATION_ERROR = 'form_validation_error',
  /** Checkout rendered the empty-cart screen. Diagnostic, first-party only. */
  CHECKOUT_EMPTY_CART_SHOWN = 'checkout_empty_cart_shown',
  /** Checkout crossed a loop threshold (payment intent requests, cart id changes or remounts) in one page load. Diagnostic, first-party only. */
  CHECKOUT_LOOP_DETECTED = 'checkout_loop_detected',
}

export enum ANALYTICS_PROPERTY {
  /** Storefront catalogs: the catalog an event happened on, and the item it concerns. */
  CatalogId = 'catalogId',
  CatalogSlug = 'catalogSlug',
  CatalogItemId = 'catalogItemId',
  CatalogSectionId = 'catalogSectionId',
  /** `listing` or `catalog`: what a clicked tile points at. */
  ItemType = 'itemType',
  /** The tab a `click_tab` landed on. */
  Tab = 'tab',
  /** The `data-category-id` the embed was installed with, when set. */
  EmbeddedCategoryId = 'embeddedCategoryId',
  BrandName = 'brandName',
  ListId = 'listId',
  ListName = 'listName',
  Products = 'products',
  BrandId = 'brandId',
  Url = 'url',
  TimeStamp = 'timestamp',
  UserAgent = 'userAgent',
  QueryParams = 'queryParams',
  SearchQuery = 'searchQuery',
  BrowserLanguage = 'browserLanguage',
  AppLanguage = 'appLanguage',
  SessionStart = 'sessionStart',
  ExperienceTitle = 'experienceTitle',
  ExperienceId = 'experienceId',
  ExperienceCategory = 'experienceCategory',
  ExperienceOrder = 'experienceOrder',
  TotalCartAmount = 'totalCartAmount',
  CurrentCartAmount = 'currentCartAmount',
  DiscountCode = 'discountCode',
  PaymentMethod = 'paymentMethod',
  BookingRequestTotalPrice = 'bookingRequestTotalPrice',
  BookingRequestStartDate = 'bookingRequestStartDate',
  BookingRequestEndDate = 'bookingRequestEndDate',
  CartExperiences = 'cartExperiences',
  CartParticipants = 'cartParticipants',
  CartConfirmationCode = 'cartConfirmationCode',
  CartExperienceCount = 'cartExperienceCount',
  IsBookable = 'isBookable',
  TotalTaxAmount = 'totalTaxAmount',
  CurrencyCode = 'currencyCode',
  AllExperiences = 'allExperiences',
  AllCategories = 'allCategories',
  CartCategories = 'cartCategories',
  CartExperienceAmount = 'cartExperienceAmount',
  ExperienceAmount = 'experienceAmount',
  ExperienceSubtotal = 'experienceSubtotal',
  PreviousUrl = 'previousUrl',
  CtaUrl = 'ctaUrl',
  FilterSelects = 'filterSelects',
  AddedAmount = 'addedAmount',
  ExperienceCount = 'experienceCount',
  SessionId = 'sessionId',
  UserId = 'userId',
  /** JSON string of diagnostic counters and state, sent with diagnostic events. */
  Diagnostics = 'diagnostics',
  CartExperienceAvgAmount = 'cartExperienceAvgAmount',
  CartExperiencesByPriceTier = 'cartExperiencesByPriceTiers',
  CartExperiencesCategoriesByPriceTier = 'cartExperiencesCategoriesByPriceTier',
  CartParticipantsCountByPriceTier = 'cartParticipantsCountByPriceTier',
  CartExperiencesSubtotalByPriceTier = 'cartExperiencesSubtotalByPriceTier',
  CartExperiencesPriceTiersCount = 'cartExperiencesPriceTiersCount',
  CartPriceTiers = 'cartPriceTiers',
  CurrentExperiencesByPriceTier = 'currentExperiencesByPriceTiers',
  CurrentExperiencesCategoriesByPriceTier = 'currentExperiencesCategoriesByPriceTier',
  CurrentParticipantsCountByPriceTier = 'currentParticipantsCountByPriceTier',
  CurrentExperiencesSubtotalByPriceTier = 'currentExperiencesSubtotalByPriceTier',
  CurrentExperiencesPriceTiersCount = 'currentExperiencesPriceTiersCount',
  CurrentPriceTiers = 'currentPriceTiers',
  MemberLoginType = 'gp_member_login_type',
  MemberLoginStatus = 'gp_login_status',
  MemberPointsBalance = 'gp_points_balance',
  MemberLevel = 'gp_level',
  MemberNumber = 'gp_number',
  LanguageId = 'language_id',
  ExperienceDate = 'experience_date',
  ExperienceLocation = 'experience_location',
  ExperienceProduct = 'experience_product',
  ExperiencePrice = 'experience_price',
  PageName = 'page_name',
  PageType = 'page_type',
  EventName = 'event_name',
  ExperienceHotel = 'experience_hotel',
  ExperienceSpiritCode = 'experience_spirit_code',
  ExperienceVendor = 'experience_vendor',
  FilterOptions = 'filter_options',
  ErrorCode = 'error_code',
  ErrorMessage = 'error_message',
}

export const AnalyticsEventConfigurationSchema = z.object({
  enabled: z.boolean(),
  property_mapping: z.record(z.string()),
});

export type AnalyticsEventConfiguration = z.infer<typeof AnalyticsEventConfigurationSchema>;

export const AnalyticsConfigurationSchema = z.object({
  defaultTracking: z.boolean().optional(),
  initializeSdk: z.boolean().optional(),
  globalVariables: z.record(z.unknown()),
  events: z.record(AnalyticsEventConfigurationSchema),
});

export type IAnalyticsConfiguration = z.infer<typeof AnalyticsConfigurationSchema>;

export const publicIntegrationConfigUrl = ({ brandId }: { brandId: string }) =>
  `v1/brands/${brandId}/integrations/analytics`;

export const TealiumCredentialSchema = z.object({
  profile: z.string(),
  account: z.string(),
  environment: z.string(),
});
export type ITealiumCredentials = z.infer<typeof TealiumCredentialSchema>;

export const AdobeCredentialSchema = z.object({
  embedUrl: z.string().url(),
});
export type IAdobeCredentials = z.infer<typeof AdobeCredentialSchema>;

export interface IAnalyticsIntegrationResponse {
  type: IntegrationName;
  config?: IAnalyticsConfiguration;
  credential?: ITealiumCredentials | IAdobeCredentials;
}

export interface IAnalyticsConfigEndpoint {
  request: void;
  response: IAnalyticsIntegrationResponse[];
}

export const AnalyticsConfigEndpoint = makeEndpoint({
  method: 'GET',
  url: publicIntegrationConfigUrl,
  responseSchema: IntegrationConfigResponseSchema,
  requestSchema: z.void(),
});

export interface IAnalyticsEventRequestSchema {
  request: z.infer<typeof AnalyticsEventRequestSchema>;
  response: void;
}

export const AnalyticsEventRequestSchema = z.object({
  providers: z.array(z.enum(Integrations[IntegrationGroup.ANALYTICS])).optional(),
  event: z.nativeEnum(ANALYTICS_EVENT),
  payload: z.record(z.string()),
});

export const AnalyticsEventSchema = AnalyticsEventRequestSchema.extend({
  brandId: z.string().uuid(),
});

export const AnalyticsEventEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/analytics/event`,
  responseSchema: z.void(),
  requestSchema: AnalyticsEventRequestSchema,
});

export const GetAnalyticsIntegrationConfigEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, integrationType }: { brandId: string; integrationType: IntegrationType }) =>
    `${integrationUrl({ brandId, integrationType })}/config`,
  responseSchema: AnalyticsConfigurationSchema,
  requestSchema: z.void(),
});

export type IGetAnalyticsIntegrationConfigEndpoint = IRestEndpointSchema<typeof GetAnalyticsIntegrationConfigEndpoint>;

export const GetAnalyticsIntegrationCredentialEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, integrationType }: { brandId: string; integrationType: IntegrationType }) =>
    `${integrationUrl({ brandId, integrationType })}/credential`,
  responseSchema: TealiumCredentialSchema,
  requestSchema: z.void(),
});

export type IGetAnalyticsIntegrationCredentialEndpoint = IRestEndpointSchema<
  typeof GetAnalyticsIntegrationCredentialEndpoint
>;
